import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';

import { isTauri } from '@shared/utils/platform.utils';
import { environment } from 'src/environments/environment';
import { ROLLBAR } from './rollbar.service';
import { ToastService } from './toast.service';
import { UpdateService } from './update.service';

/** Minutos de inactividad del usuario tras los que se instala sola una actualización ya descargada. */
export const DESKTOP_IDLE_APPLY_MINUTES = 15;

/**
 * Fallos de instalación tras los que, en la sesión en curso, se deja de instalar sola la
 * actualización por inactividad. Sin tope, un instalador roto se reintentaría en bucle cada ~15 min
 * sin que el usuario lo pidiera; la vía manual («Actualizar ahora») sigue disponible.
 */
export const MAX_INSTALL_FAILURES_FOR_AUTO_APPLY = 2;

/**
 * Superficie mínima de `Update` (`@tauri-apps/plugin-updater`) que este servicio necesita. Se
 * declara aquí para que el spec pueda falsearla sin arrastrar el plugin; la clase real la
 * satisface estructuralmente.
 */
export interface DesktopUpdateHandle {
  version: string;
  download(): Promise<void>;
  install(): Promise<void>;
  /** Libera el recurso del lado de Rust (`Update.close()`); opcional para los fakes de test. */
  close?(): Promise<void>;
}

/**
 * Eventos del usuario que reinician la cuenta de inactividad. `scroll` no burbujea hasta
 * `document`, así que un desplazamiento dentro de una tabla no se vería; `wheel` y los eventos de
 * puntero sí, y se escuchan en fase de captura para no depender de que nadie corte la propagación.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Detección de actualizaciones del shell de escritorio (Tauri) **con la app abierta**.
 *
 * `updateIfTauri()` (`src/main.ts`) solo comprueba una vez, antes de montar Angular: un usuario que
 * nunca cierra la app se quedaba en la versión vieja. Ese chequeo de arranque se conserva como red
 * de seguridad; este servicio añade el re-chequeo durante la sesión (mismo patrón que
 * `MobileUpdaterService`).
 *
 * ## Flujo
 *
 * 1. `init()` (solo en Tauri y con `environment.desktopUpdaterEnabled`) programa el chequeo cada
 *    `recheckIntervalMs` y al recuperar el foco la ventana. No comprueba al instante: el arranque
 *    ya lo hizo `main.ts`, así que cuenta como último chequeo.
 * 2. Si hay versión nueva la **descarga en segundo plano** (`Update.download()`, sin instalar) y
 *    solo entonces publica `updateAvailable` / `availableVersion`.
 * 3. `AppComponent` muestra el aviso; «Actualizar ahora» llama a `applyUpdate()`
 *    (`install()` + `relaunch()`). En Windows `install()` cierra la app al lanzar el instalador.
 * 4. Si el usuario pospone, se instala sola tras `DESKTOP_IDLE_APPLY_MINUTES` de inactividad,
 *    salvo que una pantalla sensible vete la recarga (`UpdateService.autoReloadAllowed`). La cuenta
 *    de inactividad solo empieza con la descarga lista.
 *
 * Los fallos de un chequeo en segundo plano nunca llegan al usuario (log + Rollbar); solo un fallo
 * de `applyUpdate()` iniciado por el usuario muestra un toast.
 *
 * ## Fallo de instalación
 *
 * `Update.install()` consume el recurso del lado de Rust: reintentar con el mismo handle no es
 * fiable. Ante cualquier fallo (manual o automático) se **descarta** la descarga (`close()`,
 * `updateAvailable` a `false`) y el siguiente re-chequeo, pasado el throttle, baja una fresca. Tras
 * `MAX_INSTALL_FAILURES_FOR_AUTO_APPLY` fallos la instalación por inactividad deja de armarse en
 * esa sesión (la vía manual sigue disponible).
 *
 * ## Testabilidad
 *
 * Los `import()` dinámicos de los plugins están detrás de métodos `protected` sobreescribibles.
 */
@Injectable({ providedIn: 'root' })
export class DesktopUpdaterService {
  private readonly toast = inject(ToastService);
  private readonly updateService = inject(UpdateService);
  private readonly ngZone = inject(NgZone);
  /** Opcional: solo está provisto en la app real (`app.config.ts`). */
  private readonly rollbar = inject(ROLLBAR, { optional: true });

  /** Hay una versión nueva ya descargada, lista para instalar. */
  readonly updateAvailable = signal(false);

  /** Versión semver de la actualización descargada (p. ej. `'0.120.0'`). */
  readonly availableVersion = signal('');

  /** Se está instalando/relanzando: la UI muestra un diálogo bloqueante. */
  readonly applying = signal(false);

  private pendingUpdate: DesktopUpdateHandle | null = null;
  private started = false;
  /** `stop()` es terminal: un chequeo que estaba en vuelo no debe revivir listeners al terminar. */
  private stopped = false;
  private lastCheckAt = 0;
  private inFlightCheck: Promise<void> | null = null;
  private checkFailureReported = false;
  /** Fallos de `applyUpdate()` en esta sesión; un éxito relanza la app, así que nunca se reinicia. */
  private installFailures = 0;

  /** `false` cuando, tras varios fallos, ya no se instala sola por inactividad en esta sesión. */
  get autoApplyEnabled(): boolean {
    return this.installFailures < MAX_INSTALL_FAILURES_FOR_AUTO_APPLY;
  }

  private recheckTimer: ReturnType<typeof setInterval> | null = null;
  private removeFocusListeners: (() => void) | null = null;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private removeActivityListeners: (() => void) | null = null;
  private lastActivityAt = 0;

  /** Tiempo mínimo entre dos chequeos. `protected` para ajustarlo en specs. */
  protected recheckThrottleMs: number = 30 * 60_000;

  /** Cadencia del chequeo mientras la app sigue abierta. */
  protected recheckIntervalMs: number = 30 * 60_000;

  /** Inactividad del usuario tras la que se instala sola la actualización descargada. */
  protected idleApplyMs: number = DESKTOP_IDLE_APPLY_MINUTES * 60_000;

  /** Cada cuánto se reintenta la instalación por inactividad mientras una pantalla la veta. */
  protected idleRetryMs = 60_000;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  async init(): Promise<void> {
    if (!isTauri() || !environment.desktopUpdaterEnabled || this.started) return;
    this.started = true;

    // `main.ts` ya comprobó al arrancar: el primer re-chequeo cae a los `recheckThrottleMs`.
    this.lastCheckAt = Date.now();
    this.watchWindowFocus();
    this.startPeriodicCheck();
  }

  /**
   * Re-chequeo oportunista (foco de ventana e intervalo). Se salta si ya hay una actualización
   * descargada, si se está instalando o si el último chequeo fue hace menos de `recheckThrottleMs`.
   */
  async recheck(): Promise<void> {
    if (!this.started) return;
    if (this.updateAvailable() || this.applying()) return;
    if (Date.now() - this.lastCheckAt < this.recheckThrottleMs) return;

    await this.runCheck();
  }

  /**
   * Instala la actualización descargada y relanza la app.
   *
   * `silent` es la vía automática por inactividad: un fallo ahí no se le muestra al usuario, que ni
   * siquiera pidió la acción.
   *
   * @returns `false` si no había nada descargado, ya se estaba instalando o el intento falló; en
   * ese caso la descarga se descarta (`updateAvailable` pasa a `false`) y el siguiente re-chequeo
   * baja una nueva.
   */
  async applyUpdate(options: { silent?: boolean } = {}): Promise<boolean> {
    const update = this.pendingUpdate;
    if (!update || this.applying()) return false;

    this.applying.set(true);
    this.stopIdleTracker();
    try {
      await update.install();
      const relaunch = await this.loadRelaunch();
      await relaunch();
      return true;
    } catch (err) {
      console.error('[DesktopUpdater] No se pudo instalar la actualización.', err);
      this.rollbar?.error('DesktopUpdater: no se pudo instalar la actualización', {
        version: update.version,
        error: err instanceof Error ? err.message : String(err),
      });
      this.installFailures++;
      this.discardPendingUpdate(update);
      if (!options.silent) {
        this.toast.error('No se pudo instalar la actualización. Inténtalo de nuevo más tarde.', {
          summary: 'Error de actualización',
        });
      }
      return false;
    }
  }

  /** Detiene intervalo, listeners y cuenta de inactividad. Lo usan los specs y `DestroyRef`. */
  stop(): void {
    this.stopped = true;
    if (this.recheckTimer !== null) {
      clearInterval(this.recheckTimer);
      this.recheckTimer = null;
    }
    this.removeFocusListeners?.();
    this.removeFocusListeners = null;
    this.stopIdleTracker();
  }

  // ── Puntos de carga dinámica (sobreescribibles en tests) ─────────────────

  /** `check()` de `@tauri-apps/plugin-updater`, con el mismo timeout que el chequeo de arranque. */
  protected async checkTauriUpdate(): Promise<DesktopUpdateHandle | null> {
    const { check } = await import('@tauri-apps/plugin-updater');
    return check({ timeout: 10_000 });
  }

  /** `relaunch()` de `@tauri-apps/plugin-process`. */
  protected async loadRelaunch(): Promise<() => Promise<void>> {
    const { relaunch } = await import('@tauri-apps/plugin-process');
    return relaunch;
  }

  // ── Chequeo ──────────────────────────────────────────────────────────────

  /** Chequeo sin throttle; dos disparos simultáneos (foco + intervalo) comparten la misma petición. */
  private runCheck(): Promise<void> {
    if (this.inFlightCheck) return this.inFlightCheck;

    this.inFlightCheck = this.checkAndDownload()
      .catch((err: unknown) => this.registerCheckFailure(err))
      .finally(() => {
        this.lastCheckAt = Date.now();
        this.inFlightCheck = null;
      });

    return this.inFlightCheck;
  }

  private async checkAndDownload(): Promise<void> {
    const update = await this.checkTauriUpdate();
    if (!update) return;

    if (this.stopped) return this.release(update);
    try {
      await update.download();
    } catch (err) {
      this.release(update);
      throw err;
    }
    if (this.stopped) return this.release(update);

    this.pendingUpdate = update;
    this.availableVersion.set(update.version);
    this.updateAvailable.set(true);
    this.startIdleTracker();
  }

  /**
   * Descarta una descarga cuyo `install()` falló: el handle ya está consumido. Cuenta el fallo como
   * chequeo (`lastCheckAt`) para que la siguiente descarga espere al throttle en vez de reintentarse
   * en caliente contra un instalador que acaba de fallar.
   */
  private discardPendingUpdate(update: DesktopUpdateHandle): void {
    this.release(update);
    this.pendingUpdate = null;
    this.availableVersion.set('');
    this.updateAvailable.set(false);
    this.applying.set(false);
    this.lastCheckAt = Date.now();
  }

  /** Libera el handle del plugin cuando no se va a instalar; un fallo al cerrarlo es irrelevante. */
  private release(update: DesktopUpdateHandle): void {
    void update.close?.().catch(() => undefined);
  }

  /**
   * Un fallo en segundo plano se registra pero no se muestra. Sin conexión es esperado y no se
   * reporta; con conexión es un problema de despliegue y se reporta una vez por sesión.
   */
  private registerCheckFailure(err: unknown): void {
    console.error('[DesktopUpdater] No se pudo comprobar/descargar la actualización.', err);

    if (!navigator.onLine || this.checkFailureReported) return;
    this.checkFailureReported = true;
    this.rollbar?.warning('DesktopUpdater: no se pudo comprobar/descargar la actualización', {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  private startPeriodicCheck(): void {
    if (this.recheckTimer !== null) return;
    this.ngZone.runOutsideAngular(() => {
      this.recheckTimer = setInterval(() => void this.recheck(), this.recheckIntervalMs);
    });
  }

  /** Re-chequea cuando la ventana recupera el foco o vuelve a ser visible. */
  private watchWindowFocus(): void {
    const onFocus = () => void this.recheck();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void this.recheck();
    };

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVisibility);
    });
    this.removeFocusListeners = () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  // ── Inactividad ──────────────────────────────────────────────────────────

  /**
   * Arranca la cuenta de inactividad. Los eventos del usuario solo anotan la hora de la última
   * actividad (barato, sin recrear temporizadores en cada `mousemove`); al vencer el temporizador
   * se comprueba si de verdad pasó el tiempo. Corre fuera de la zona de Angular para que esos
   * eventos no disparen change detection.
   */
  private startIdleTracker(): void {
    if (this.idleTimer !== null || !this.pendingUpdate || this.applying()) return;
    if (!this.autoApplyEnabled) return;

    this.lastActivityAt = Date.now();
    const onActivity = () => {
      this.lastActivityAt = Date.now();
    };

    this.ngZone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, onActivity, { passive: true, capture: true }));
      this.armIdleTimer(this.idleApplyMs);
    });
    this.removeActivityListeners = () =>
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, onActivity, { capture: true }));
  }

  private stopIdleTracker(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.removeActivityListeners?.();
    this.removeActivityListeners = null;
  }

  private armIdleTimer(ms: number): void {
    this.idleTimer = setTimeout(() => this.onIdleTick(), ms);
  }

  private onIdleTick(): void {
    this.idleTimer = null;
    const remaining = this.idleApplyMs - (Date.now() - this.lastActivityAt);
    if (remaining > 0) {
      this.armIdleTimer(remaining); // hubo actividad desde que se armó el temporizador
      return;
    }
    if (!this.updateService.autoReloadAllowed()) {
      this.armIdleTimer(this.idleRetryMs); // una pantalla sensible veta la recarga: reintentar luego
      return;
    }
    this.ngZone.run(() => void this.applyUpdate({ silent: true }));
  }
}
